import React, { useContext, useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Case } from "./types/type";
import { Button } from "./components/Button";
import { InterviewContext } from "src/contexts/InterviewContext";
import { useLoading } from "src/contexts/LoadingContext";
import { useAuth } from "src/contexts/AuthContext";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";
import { useFetchCases } from "src/hooks/useFetchCases";
import { InterviewCase } from "../CaseSelectionPage";
import ErrorMessage from "src/components/ErrorMessage";
import CreditConfirmationDialog from "../CaseSelectionPage/CreditConfirmationDialog";

export const PopularCase = () => {
  const [cases, setCases] = useState<InterviewCase[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const stableIds = React.useMemo<number[]>(() => [101], [101]);
  const { data, isLoading, isError, error } = useFetchCases(stableIds);

  useEffect(() => {
    if (data && data.length > 0) {
      console.log("Fetched cases", data);
      setCases(data);
    }
  }, [data]);
  const navigate = usePartnerAwareNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const { handleTimeBasedLoading } = useLoading();
  const { startInterviewHandler, settIsEndHandler, checkInterviewCredits } =
    useContext(InterviewContext)!;
  const { firebaseUser } = useAuth();
  const [showCreditConfirmation, setShowCreditConfirmation] = useState(false);

  const handleStartMockInterview = async () => {
    setErrorMessage(null);
    const caseId = 101;
    try {
      // First check if user has enough credits before showing the confirmation dialog
      const hasCredits = await checkInterviewCredits(firebaseUser?.uid ?? "");

      if (!hasCredits) {
        setErrorMessage(
          "You don't have enough interview credits. Purchase more to continue."
        );
        return;
      }

      // Show confirmation dialog
      setShowCreditConfirmation(true);
    } catch (err) {
      console.error("Error checking credits:", err);
      setErrorMessage(
        "An error occurred while checking your credits. Please try again."
      );
    }
  };

  const handleConfirmStart = async () => {
    try {
      const caseId = 101;
      // If they have credits, continue with loading and starting the interview
      const result = await handleTimeBasedLoading(
        startInterviewHandler(
          firebaseUser?.uid ?? "",
          caseId,
          false,
          false,
          ""
        ),
        {
          totalDuration: 3500,
          messages: [
            "Initializing your mock interview...",
            "Setting up your case materials...",
            "Preparing interview environment...",
            "Almost ready to begin...",
          ],
          onComplete: () => {
            console.log("Loading complete");
          },
        }
      );

      console.log("Interview start result:", result);

      // Check if result is an error object
      if (typeof result === "object" && "error" in result) {
        setErrorMessage(result.error);
        return;
      }

      if (result && typeof result === "string" && result.length > 0) {
        const matchingCase = cases[0];
        console.log("Interview started successfully!");
        settIsEndHandler(false);
        navigate(`/mock-case?interviewSessionId=${result}`, {
          state: { interviewCase: matchingCase },
        });
      } else {
        setErrorMessage("Interview could not be started. Please try again.");
      }
    } catch (err) {
      console.error("Error starting interview:", err);
      setErrorMessage(
        "An error occurred while starting the interview. Please try again."
      );
    } finally {
      setShowCreditConfirmation(false);
    }
  };

  const popularCase: Case = {
    style: "Interviewer-led",
    industry: "Materials Trade",
    estimatedTime: "45 min",
  };

  return (
    <div
      className="
          relative
          bg-white
          rounded
          p-8
          transition-transform
          duration-500
          group
          overflow-hidden
          flex
          flex-col
          justify-between
          h-full
        "
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        boxShadow: isHovered
          ? "0 8px 32px rgba(27, 88, 244, 0.15)"
          : "0 4px 16px rgba(0, 0, 0, 0.05)",
      }}
    >
      {/* Gradient background */}
      <div
        className="absolute transition-transform duration-500"
        style={{
          background:
            "linear-gradient(145deg, #FFF 10.21%, var(--color-primary) 85.25%)",
          borderRadius: "50%",
          opacity: 0.95,
          right: "-15%",
          bottom: "-34%",
          width: "70%",
          height: "70%",
          transform: `scale(${isHovered ? 1.02 : 1})`,
          transformOrigin: "bottom right",
        }}
      />

      {/* Error message display */}
      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
          fullScreen={true}
        />
      )}

      {/* Credit Confirmation Dialog */}
      <CreditConfirmationDialog
        isOpen={showCreditConfirmation}
        onClose={() => setShowCreditConfirmation(false)}
        onConfirm={handleConfirmStart}
      />

      {/* Top content */}
      <div>
        <h2 className="text-2xl font-bold mb-8 text-black">
          Get Start with Popular Case
        </h2>

        <div className="grid grid-cols-3 gap-8 mb-8">
          {[
            { label: "Style", value: popularCase.style },
            { label: "Industry", value: popularCase.industry },
            {
              label: "Estimated Time",
              value: popularCase.estimatedTime,
            },
          ].map((item) => (
            <div key={item.label}>
              <h3 className="text-slate-600 mb-2 font-medium">{item.label}</h3>
              <p className="font-semibold text-black">{item.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom: Button */}
      <div className="mt-auto">
        <Button
          onClick={handleStartMockInterview}
          className="
    group
    relative
    inline-flex
    items-center
    justify-center
    border
    border-gray-300
    px-8
    py-2
    hover:bg-primary
    text-black
    rounded
    bg-white
    transition-colors
    duration-100
    hover:bg-primary/90
    hover:text-white
  "
        >
          <span className="transition-all">Start</span>
          <ArrowRight
            size={18}
            className="
      absolute
      right-4
      opacity-0
      -translate-x-2
      transition-all
      duration-300
      group-hover:opacity-100
      group-hover:translate-x-2
    "
          />
        </Button>
      </div>
    </div>
  );
};
